/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useRef } from 'react';
import { getControlledPaginationProps, getUncontrolledPaginationProps } from './logic/pagination';
import { useTableV2 } from './logic/table-hook';
import { Root, TableV2DefaultRenderer } from './render/default';
import { TableCards } from './render/table-cards';
import { TableV2Props } from './types';

/**
 * A custom table component that's built on top of @tanstack/react-table. For basic intro and docs,
 * see https://tanstack.com/table/v8/docs/guide/introduction.
 *
 * The table is separated into a headless table logic (initialied in useTableV2 hook), and our custom
 * renderers that actually draw the table based on the table logic.
 *
 * With any questions on advanced usage and customization, ML and OA can help.
 */

export function TableV2<T extends object, ColumnId extends string = string>(props: TableV2Props<T, ColumnId>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const rootWidth = rootRef.current?.getBoundingClientRect().width;

  const mobileMode =
    rootWidth && props.mobileMode && rootWidth < props.mobileMode.breakpoint ? props.mobileMode.mode : undefined;

  const { table, handleTableExport } = useTableV2(props, mobileMode);

  const paginatorProps = props.pagination
    ? getControlledPaginationProps(props.pagination)
    : getUncontrolledPaginationProps(table);

  const content = (() => {
    if (mobileMode === 'cards')
      return (
        <TableCards
          {...{
            ...props,
            table,
          }}
        />
      );
    return (
      <TableV2DefaultRenderer
        {...{
          ...props,
          paginatorProps,
          handleTableExport,
          table,
        }}
      />
    );
  })();

  return (
    <Root ref={rootRef} className={props.className} style={props.style}>
      {content}
    </Root>
  );
}
